import { Component, OnInit, Output, EventEmitter, OnDestroy, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { CommonService } from '../../../core/services/common.service';
import { EMPTY, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { ContentMapperService } from '../../../core/services/content-mapper-service';
import { nonZeroIntegerValidator } from '../../../../shared/validators/nonZeroIntegerValidator';
import { SharedDropdownComponent } from '../../../../shared/components/shared-dropdown-component/shared-dropdown-component';
import { segment_endpoint } from '../../../data/api-endpoint/segment-endpoint';
import { ToastrService } from 'ngx-toastr';
import { Loader } from '../../../../shared/components/loader/loader';
import { ContentPoolStatusComponent } from '../../../../shared/components/content-pool-status/content-pool-status';
import { RuleBasedMappingComponent, RuleLogic } from './rule-based-mapping/rule-based-mapping';

type MappingTab = 'segment-list' | 'rule-based';

@Component({
  selector: 'app-create-segment',
  imports: [CommonModule, ReactiveFormsModule, SharedDropdownComponent, Loader, ContentPoolStatusComponent, RuleBasedMappingComponent],
  templateUrl: './create-segment.html',
  styleUrl: './create-segment.scss'
})
export class CreateSegment implements OnInit, OnDestroy {

  id: any
  segment_id: any = ''
  editMode: boolean = false;
  hidePartnerId: boolean = false;
  loading = false;
  isOpen: boolean = false

  isViewForm: boolean = true;
  specifyCustomSegmentId: boolean = false;
  customSegmentIdSubmitAttempted: boolean = false;
  customSegmentIdStatus: 'idle' | 'checking' | 'available' | 'unavailable' = 'idle';
  private customSegmentIdSub: Subscription = new Subscription();

  activeTab: MappingTab = 'segment-list';
  segmentListConditionMode: 'OR' | 'AND' = 'OR';
  initialRuleLogic: RuleLogic | null = null;
  currentRuleLogic: RuleLogic | null = null;
  ruleBasedSubmitted = false;
  isTabSwitchLocked = false;

  private initialFormSnapshot: {
    provider_element_id: number;
    partner_ids: string[];
    content_pool_ids: number[];
    ttd_segments: string[];
    is_rule_based: boolean;
    rule_logic_key: string;
  } | null = null;

  @ViewChild(RuleBasedMappingComponent) private ruleBasedRef?: RuleBasedMappingComponent;
  @ViewChild('customSegmentIdInput') private customSegmentIdInputRef?: ElementRef<HTMLInputElement>;

  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  contentPool: any = []
  segmentList: any = []
  previousSegmentData: any = {}
  contentPoolStatus: any[] = []

  customWidth = signal<boolean>(false);

  createSegmentForm: FormGroup<any> = new FormGroup<any>({});

  private subscription: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private contentMapper: ContentMapperService,
    private toastr: ToastrService
  ) {

  }

  ngOnInit(): void {
    this.subscription = this.commonService.getSegmentModal().subscribe((config: any) => {

      if (config.isOpen && config.type === 'Edit Segment' && config.id) {
        this.editMode = true;
        this.isViewForm = false;
        this.segment_id = config.segment_id;
        this.id = config.id;
        this.getCurrentSegment(config.id);
      } else if (config.isOpen && config.type === 'Create Segment') {
        this.isViewForm = false;
        this.editMode = false;
      } else {
        this.segment_id = config.segment_id;
        this.id = config.id;
        if (config.isOpen && config.id) this.getCurrentSegment(config.id);
      }

      this.isOpen = config.isOpen;
      this.hidePartnerId = false;
      this.initializeForm();

      if (config.isOpen && config.id) {
        const configSegmentRule = config.segment_rule && typeof config.segment_rule === 'object'
          ? config.segment_rule : null;
        const configIsRuleBased = !!config.is_rule_based || !!(configSegmentRule?.groups?.length);
        
        if (configIsRuleBased) {
          this.activeTab = 'rule-based';
          this.initialRuleLogic = configSegmentRule;
          this.currentRuleLogic = configSegmentRule;
        }
      }
      this.commonService.updateShareContentPoolStatus({})

      if(this.isOpen){
        this.getContentPool()
        this.getSegmentListforMapping()
      }
    });
  }

  onEdit(): void {
    this.isViewForm = false;
    this.editMode = true;
  }


  private initializeForm(): void {
    this.createSegmentForm = this.fb.group({
      name: ['', Validators.required],
      segment_type: ['custom', Validators.required],
      provider_element_id: ['', [Validators.required, nonZeroIntegerValidator]],
      partner_id: [''],
      custom_segment_id: [''],
      content_pool: this.fb.array([]),
      segment_list: this.fb.array([])
    });
    this.activeTab = 'segment-list';
    this.segmentListConditionMode = 'OR';
    this.initialRuleLogic = null;
    this.currentRuleLogic = null;
    this.ruleBasedSubmitted = false;
    this.isTabSwitchLocked = false;
    this.specifyCustomSegmentId = false;
    this.customSegmentIdSubmitAttempted = false;
    this.customSegmentIdStatus = 'idle';
    this.initialFormSnapshot = null;
    this.customSegmentIdSub.unsubscribe();
  }

  onCustomSegmentIdToggle(): void {
    this.specifyCustomSegmentId = !this.specifyCustomSegmentId;
    if (this.specifyCustomSegmentId) {
      this.subscribeToCustomSegmentIdChanges();
    } else {
      this.customSegmentIdSub.unsubscribe();
      this.createSegmentForm.get('custom_segment_id')?.setValue('');
      this.customSegmentIdStatus = 'idle';
    }
  }

  private checkCustomSegmentIdExists(segmentId: string) {
    const encoded = encodeURIComponent(segmentId);
    return this.contentMapper
      .get<any>(`${segment_endpoint.validate}?segment_id=${encoded}`)
      .pipe(map((response: any) => !!response?.results?.is_segment_id_exist));
  }

  private subscribeToCustomSegmentIdChanges(): void {
    this.customSegmentIdSub.unsubscribe();
    const ctrl = this.createSegmentForm.get('custom_segment_id')!;
    this.customSegmentIdSub = ctrl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((value: string) => {
        const trimmed = (value || '').trim();
        if (trimmed.length >= 12 && trimmed.length <= 29) {
          this.customSegmentIdStatus = 'checking';
        } else {
          this.customSegmentIdStatus = 'idle';
        }
      }),
      switchMap((value: string) => {
        const trimmed = (value || '').trim();
        if (trimmed.length >= 12 && trimmed.length <= 29) {
          const fullId = 'sp_' + trimmed;
          return this.checkCustomSegmentIdExists(fullId).pipe(
            map((exists) => ({ exists }))
          );
        }
        return EMPTY;
      })
    ).subscribe({
      next: ({ exists }: any) => {
        this.customSegmentIdStatus = exists ? 'unavailable' : 'available';
      },
      error: () => {
        this.customSegmentIdStatus = 'idle';
      },
    });
  }

  onCustomSegmentIdFocusChange(): void {
    const suffix = (this.createSegmentForm.get('custom_segment_id')?.value || '').trim();
    if (suffix.length >= 12 && suffix.length <= 29) {
      const fullId = 'sp_' + suffix;
      this.customSegmentIdStatus = 'checking';
      this.checkCustomSegmentIdExists(fullId)
        .pipe(catchError(() => of(null)))
        .subscribe((exists) => {
          if (exists === null) {
            this.customSegmentIdStatus = 'idle';
            return;
          }
          this.customSegmentIdStatus = exists ? 'unavailable' : 'available';
        });
    }
  }

  get ContentPoolFormArray(): FormArray {
    return this.createSegmentForm.get('content_pool') as FormArray;
  }

  get SegmentListFormArray(): FormArray {
    return this.createSegmentForm.get('segment_list') as FormArray;
  }

  onTabSwitch(tab: MappingTab): void {
    if (this.isTabSwitchLocked) return;
    this.activeTab = tab;
  }

  onConditionModeChange(mode: 'OR' | 'AND'): void {
    this.segmentListConditionMode = mode;
  }

  onRuleLogicChange(logic: RuleLogic): void {
    this.currentRuleLogic = logic;
  }

  onSegmentTypeChange(event: Event) {
    const role = (event.target as HTMLSelectElement).value;
    const valueControl = this.createSegmentForm.get('partner_id');

    if (role === 'standard') {
      this.hidePartnerId = true;
      this.customWidth.set(false);
      valueControl?.setValue('');
      valueControl?.markAsUntouched();
      valueControl?.markAsPristine();
      valueControl?.clearValidators();
      valueControl?.updateValueAndValidity();
    } else {
      this.hidePartnerId = false;
      this.customWidth.set(true);
      valueControl?.setValidators([Validators.required]);
      valueControl?.updateValueAndValidity();
    }
  }

  addItemInFormArray(e: Event, data: Record<string, any>, type: string) {
    const isChecked = (e.target as HTMLInputElement).checked;
    const formArray = this.createSegmentForm.get(type) as FormArray;
    if (isChecked) {
      formArray.push(this.fb.control(data));
    } else {
      const index = formArray.controls.findIndex((x: any) => {
        return x.value['value'] === data['value']
      });
      formArray.removeAt(index)
    }
  }

  checkedAll(checked: boolean, data: any, type: string) {
    const formArray = this.createSegmentForm.get(type) as FormArray;
    if (checked) {
      formArray.clear();
      data.forEach((element: any) => {
        formArray.push(this.fb.control(element));
      });
    } else {
      formArray.clear();
    }
  }

  removeItemFormArray(index: number, type: string) {
    const formArray = this.createSegmentForm.get(type) as FormArray;
    formArray.removeAt(index)
  }


  copyPartnerId(): void {
    const partnerId = this.createSegmentForm.get('partner_id')?.value;
    if (!partnerId) {
      return;
    }
    navigator.clipboard?.writeText(partnerId).then(() => {
      this.toastr.success('Partner ID copied to clipboard', 'Success');
    }).catch(() => {
      this.toastr.error('Failed to copy Partner ID', 'Error');
    });
  }

  copyProviderElementId(): void {
    const providerElementId = this.createSegmentForm.get('provider_element_id')?.value;
    if (!providerElementId) {
      return;
    }
    navigator.clipboard?.writeText(String(providerElementId)).then(() => {
      this.toastr.success('Provider Element ID copied to clipboard', 'Success');
    }).catch(() => {
      this.toastr.error('Failed to copy Provider Element ID', 'Error');
    });
  }


  copyExample(value:string,type:string){
    navigator.clipboard?.writeText(value).then(() => {
      this.toastr.success(`${type} copied to clipboard`, 'Success');
    }).catch(() => {
      this.toastr.error(`Failed to copy ${type}`, 'Error');
    });

  }


  getCurrentSegment(id: string | number): void {
    this.loading = true;
    this.contentMapper.get(`${segment_endpoint.segment}/${id}`).subscribe({
      next: (response: any) => {
        const { segment } = response.results;

        this.previousSegmentData = structuredClone(segment);
        this.hidePartnerId = false;

        const initialStatus = segment.hasFirstApproval || segment.approval_pending ? 'active' : 'pending';

        this.customWidth.set(segment.segment_type === 'custom');
        this.hidePartnerId = segment.segment_type === 'custom' ? false : true;

        this.patchBasicFormFields(segment);

        this.clearFormArrays();
        // Content Pool selection is shared across both mapping flows (segment-list and rule-based)
        this.populateContentPoolArray(segment);

        const statusArrays = this.buildStatusArrays(segment, initialStatus);

        this.isTabSwitchLocked = !!segment.hasFirstApproval || !!segment.approval_pending;

        const httpSegmentRule = segment.segment_rule && typeof segment.segment_rule === 'object'
          ? segment.segment_rule : null;
        const ruleGroups = httpSegmentRule?.groups;
        const isRuleBased = !!segment.is_rule_based || !!(ruleGroups?.length);

        if (isRuleBased) {
          this.activeTab = 'rule-based';
          this.initialRuleLogic = httpSegmentRule;
          this.currentRuleLogic = httpSegmentRule;
        } else if (this.currentRuleLogic && this.currentRuleLogic.groups?.length) {
          this.activeTab = 'rule-based';
          this.initialRuleLogic = this.currentRuleLogic;
        } else if (this.initialRuleLogic && this.initialRuleLogic.groups?.length) {
          this.activeTab = 'rule-based';
          this.currentRuleLogic = this.initialRuleLogic;
        } else if (this.activeTab === 'rule-based') {
          // Config already determined this is rule-based (from listing data).
          // Don't override to segment-list just because the HTTP response
          // lacks is_rule_based (e.g. stale dist build). Keep the tab and
          // any rule logic already loaded from the config.
        } else {
          this.activeTab = 'segment-list';
          this.initialRuleLogic = null;
          this.populateFormArrays(segment);
        }

        this.commonService.updateShareContentPoolStatus(statusArrays);
        this.captureInitialSnapshot();
        this.loading = false;
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error occurred while fetching current segment', error.message);
      }
    });
  }

  private patchBasicFormFields(segment: any): void {
    this.createSegmentForm.patchValue({
      name: segment.name,
      segment_id: segment.segment_id,
      segment_type: segment.segment_type,
      provider_element_id: segment.provider_element_id,
      partner_id: segment.partner_ids?.join(',') || '',
    });
  }


  private clearFormArrays(): void {
    (this.createSegmentForm.get('content_pool') as FormArray).clear();
    (this.createSegmentForm.get('segment_list') as FormArray).clear();
  }

  private populateContentPoolArray(segment: any): void {
    const contentPoolArray = this.createSegmentForm.get('content_pool') as FormArray;
    const removedContentIds = segment.removedContentIds?.map((item: any) => item.id) || [];

    const activeContentIds = [
      ...(segment.activeContentIds || []),
      ...(segment.addedContentIds || []),
    ].filter((item: any) => !removedContentIds.includes(item.id));

    activeContentIds.forEach((item: any) => {
      contentPoolArray.push(this.fb.control({
        name: item?.name || '',
        value: item?.id,
      }));
    });
  }


  private mapToStatusItem(status: 'active' | 'pending' | 'deleted') {
    return (item: any) => ({
      id: item.id ?? item,
      name: item.name ?? item,
      status
    });
  }


  private safeMapArray(arr: any[] | undefined, status: 'active' | 'pending' | 'deleted'): any[] {
    return arr?.length ? arr.map(this.mapToStatusItem(status)) : [];
  }


  private buildStatusArrays(segment: any, initialStatus: 'active' | 'pending') {
    const contentPoolStatus = [
      ...this.safeMapArray(segment.activeContentIds, 'active'),
      ...this.safeMapArray(segment.addedContentIds, 'pending'),
      ...this.safeMapArray(segment.removedContentIds, 'deleted')
    ];

    const ttdSegmentStatus = [
      ...this.safeMapArray(segment.ttd_segments, initialStatus),
      ...this.safeMapArray(segment.added_ttd_segments, 'pending'),
      ...this.safeMapArray(segment.removed_ttd_segments, 'deleted')
    ];

    const partnerIdStatus = [
      ...this.safeMapArray(segment.partner_ids, initialStatus),
      ...this.safeMapArray(segment.updated_partner_ids, 'pending'),
      ...this.safeMapArray(segment.removed_partner_ids, 'deleted')
    ];

    const providerElementIdStatus = [
      ...this.safeMapArray(segment.provider_element_id, initialStatus),
      ...this.safeMapArray(segment.updated_provider_element_id, initialStatus),
    ];

    return {
      contentPoolStatus,
      ttdSegmentStatus,
      partnerIdStatus,
      providerElementIdStatus
    };
  }


  private populateFormArrays(segment: any): void {
    const ttdSegmentPoolArray = this.createSegmentForm.get('segment_list') as FormArray;

    const removedParnterIds = segment.removed_partner_ids?.map((item: any) => item.id) || [];

    const activeSegments = segment.updated_ttd_segments?.length
      ? segment.updated_ttd_segments
      : (segment.ttd_segments || []);

    activeSegments.forEach((item: any) => {
      ttdSegmentPoolArray.push(this.fb.control({
        name: item.name,
        value: item.id
      }));
    });

    const latestPartnerIds = (segment.updated_partner_ids?.length
      ? segment.updated_partner_ids
      : (segment.partner_ids || []))
      .filter((item:any) => !removedParnterIds.includes(item));

    this.createSegmentForm.get('partner_id')?.patchValue(latestPartnerIds.join(','));
  }


  getContentPool() {
    this.contentMapper.get(segment_endpoint.contentPool).subscribe({
      next: (response: any) => {
        const { contentPools = [] } = response.results;
        this.contentPool = contentPools.map((item: any) => {
          return {
            name: item.name,
            value: +item.id
          }
        });
      },
      error: (error: any) => {
        console.error('Error occurred while fetching content pool list', error.message);
      }
    });
  }

  getSegmentListforMapping() {
    this.contentMapper.get(`${segment_endpoint.segment}/ttd-segments?segment_id=${this.segment_id}`).subscribe({
      next: (response: any) => {
        const { segments = [] } = response.results;
        this.segmentList = segments.map((item: any) => {
          return {
            name: item.name,
            value: item.segment_id
          }
        })
      },
      error: (error: any) => {
        console.error('Error occurred while fetching segment list', error.message);
      }

    })
  }


  onCancel(): void {
    this.isViewForm = true;
    this.createSegmentForm.reset();
    this.initializeForm();
    this.editMode = false;
    this.hidePartnerId = false;
    this.customWidth.set(false);
    this.loading = false;
    this.id = null;
    this.previousSegmentData = {};
    this.activeTab = 'segment-list';
    this.segmentListConditionMode = 'OR';
    this.initialRuleLogic = null;
    this.currentRuleLogic = null;
    this.ruleBasedSubmitted = false;
    this.close.emit();
    this.commonService.updateShareContentPoolStatus([])
  }

  checkIsErrorMessageValid(error: any) {
    if(typeof error?.error?.message === 'string') {
      return error?.error?.message;
    }
    return 'An unknow error occured';
  }

  private validateRuleBasedTab(): boolean {
    // Always set so the child highlights every empty condition with a red border
    this.ruleBasedSubmitted = true;

    if (!this.ruleBasedRef?.hasAnySegment()) {
      this.toastr.error('Please add at least one segment to a rule group', 'Validation Error');
      return false;
    }

    if (this.ruleBasedRef?.hasEmptyConditions()) {
      this.toastr.error('Each condition must have at least one segment', 'Validation Error');
      return false;
    }

    return true;
  }

  get isRuleBasedTabActive(): boolean {
    return this.activeTab === 'rule-based';
  }

  private normalizeRuleLogic(logic: RuleLogic | null): string {
    if (!logic) return '';
    return JSON.stringify({
      operator: logic.operator,
      groups: logic.groups.map(g => ({
        operator: g.operator,
        conditions: g.conditions.map(c => ({
          field: c.field,
          operator: c.operator,
          value: [...c.value].sort(),
        })),
      })),
    });
  }

  private captureInitialSnapshot(): void {
    const { provider_element_id, partner_id, content_pool, segment_list } = this.createSegmentForm.getRawValue();
    this.initialFormSnapshot = {
      provider_element_id: +provider_element_id,
      partner_ids: (partner_id || '').split(',').map((s: string) => s.trim()).filter(Boolean).sort(),
      content_pool_ids: (content_pool as any[]).map((item: any) => +item.value).sort((a: number, b: number) => a - b),
      ttd_segments: (segment_list as any[]).map((item: any) => item.value as string).sort(),
      is_rule_based: this.activeTab === 'rule-based',
      rule_logic_key: this.normalizeRuleLogic(this.initialRuleLogic),
    };
  }

  get hasFormChanged(): boolean {
    if (!this.initialFormSnapshot) return false;
    const snap = this.initialFormSnapshot;
    const { provider_element_id, partner_id, content_pool, segment_list } = this.createSegmentForm.getRawValue();

    if (+provider_element_id !== snap.provider_element_id) return true;

    const currentPartnerIds = (partner_id || '').split(',').map((s: string) => s.trim()).filter(Boolean).sort();
    if (JSON.stringify(currentPartnerIds) !== JSON.stringify(snap.partner_ids)) return true;

    const currentContentPoolIds = (content_pool as any[]).map((item: any) => +item.value).sort((a: number, b: number) => a - b);
    if (JSON.stringify(currentContentPoolIds) !== JSON.stringify(snap.content_pool_ids)) return true;

    const currentIsRuleBased = this.activeTab === 'rule-based';
    if (currentIsRuleBased !== snap.is_rule_based) return true;

    if (currentIsRuleBased) {
      if (this.normalizeRuleLogic(this.currentRuleLogic) !== snap.rule_logic_key) return true;
    } else {
      const currentTtdSegments = (segment_list as any[]).map((item: any) => item.value as string).sort();
      if (JSON.stringify(currentTtdSegments) !== JSON.stringify(snap.ttd_segments)) return true;
    }

    return false;
  }

  get isSubmitDisabled(): boolean {
    if (this.loading || this.createSegmentForm.invalid) return true;
    if (this.editMode && !this.hasFormChanged) return true;
    if (this.specifyCustomSegmentId) {
      const suffix = (this.createSegmentForm.get('custom_segment_id')?.value || '').trim();
      if (suffix.length > 29) return true;
      if (this.customSegmentIdStatus === 'unavailable') return true;
      if (this.customSegmentIdStatus === 'checking') return true;
    }
    if (this.activeTab === 'rule-based') {
      return !this.currentRuleLogic || !this.currentRuleLogic.groups?.length;
    }
    const contentPool = this.createSegmentForm.get('content_pool')?.value || [];
    const segmentList = this.createSegmentForm.get('segment_list')?.value || [];
    return contentPool.length === 0 && segmentList.length === 0;
  }

  onSubmit() {

    if (this.createSegmentForm.invalid) {
      this.toastr.error('Please fill all required fields correctly', 'Validation Error');
      return
    }
    const { name, segment_type, provider_element_id, partner_id, content_pool, segment_list, custom_segment_id } = this.createSegmentForm.getRawValue()

    const partnerId = partner_id ?  partner_id.split(',').map((item: string) => item.trim()) : [];

    const content_pool_ids = content_pool.map((item: any) => +item.value)

    if (this.specifyCustomSegmentId) {
      this.customSegmentIdSubmitAttempted = true;
    }

    if (this.specifyCustomSegmentId) {
      const suffix = (custom_segment_id || '').trim();
      if (suffix.length < 12 || suffix.length > 29) {
        this.customSegmentIdInputRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.customSegmentIdInputRef?.nativeElement.focus();
        return;
      }
      if (this.customSegmentIdStatus === 'unavailable') {
        this.customSegmentIdInputRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.customSegmentIdInputRef?.nativeElement.focus();
        return;
      }
    }

    if (segment_type == 'custom' && partnerId.length == 0) {
      this.toastr.error('Please provide at least one Partner ID', 'Validation Error');
      return;
    }

    let final_data: any;

    if (this.activeTab === 'rule-based') {
      if (!this.validateRuleBasedTab()) return;

      final_data = {
        name: name.trim(),
        segment_type,
        provider_element_id: +provider_element_id,
        partner_ids: partnerId,
        content_pool_ids,
        ttd_segments: [],
        is_rule_based: true,
        segment_rule: this.currentRuleLogic,
      };
    } else {
      if (segment_list.length == 0 && content_pool_ids.length == 0) {
        this.toastr.error('Please select at least one content pool or segment', 'Validation Error');
        return;
      }

      final_data = {
        name: name.trim(),
        segment_type,
        provider_element_id: +provider_element_id,
        partner_ids: partnerId,
        content_pool_ids,
        ttd_segments: segment_list.map((item: Record<string, string>) => item['value']),
        is_rule_based: false,
      };
    }

    if (this.specifyCustomSegmentId) {
      final_data.segment_id = 'sp_' + (custom_segment_id || '').trim();
    }

    if (segment_type == 'standard') {
      delete final_data.partner_ids;
    }


    this.loading = true

    this.contentMapper.post(segment_endpoint.segment, final_data).subscribe({
      next: (_: any) => {
        this.loading = false;
        this.toastr.success('Segment created successfully!', 'Success');
        this.reload.emit();
        this.onCancel();
      }, error: (error: any) => {
        this.loading = false;
        this.toastr.error(this.checkIsErrorMessageValid(error), 'Error');
      }
    });

  }

  updateSegment() {

    if (this.createSegmentForm.invalid) {
      this.toastr.error('Please fill all required fields correctly', 'Validation Error');
      return
    }
    const { provider_element_id, partner_id, content_pool, segment_type, segment_list } = this.createSegmentForm.getRawValue();
    const partnerId = partner_id ?  partner_id.split(',').map((item: string) => item.trim()) : [];

    if (segment_type == 'custom' && partnerId.length == 0) {
      this.toastr.error('Please provide at least one Partner ID', 'Validation Error');
      return
    }

    const content_pool_ids = content_pool.map((item: any) => +item.value);

    let final_data: any;

    if (this.activeTab === 'rule-based') {
      if (!this.validateRuleBasedTab()) return;

      final_data = {
        provider_element_id: +provider_element_id,
        partner_ids: partnerId,
        content_pool_ids,
        ttd_segments: [],
        is_rule_based: true,
        segment_rule: this.currentRuleLogic,
      };
    } else {
      if (segment_list.length == 0 && content_pool.length == 0) {
        this.toastr.error('Please select at least one content pool or segment', 'Validation Error');
        return;
      }

      final_data = {
        provider_element_id: +provider_element_id,
        partner_ids: partnerId,
        content_pool_ids,
        ttd_segments: segment_list.map((item: Record<string, string>) => item['value']),
        is_rule_based: false,
      };
    }

    if (Object.keys(final_data).length === 0) {
      this.reload.emit();
      return;
    }

    this.loading = true;

    this.contentMapper.patch(segment_endpoint.segment, { ...final_data, id: this.id }).subscribe({
      next: (_: any) => {
        this.loading = false;
        this.toastr.success('Segment updated successfully!', 'Success');
        this.reload.emit();
        this.onCancel();
      }, error: (error: any) => {
        this.loading = false;
        this.toastr.error(this.checkIsErrorMessageValid(error), 'Error');
      }
    });

  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.customSegmentIdSub.unsubscribe();
  }

}
